import { PublicClientApplication } from '@azure/msal-browser'
import { appInsights } from 'src/AppInsights'
import { msalInstance } from 'src/auth/msal'
import {
  setBidAnalysisFilters,
  setCurrencySymbol,
  setDefaultOwnerFilter,
} from 'src/redux/slices/bidAnalysisFilters.slice'
import { setRole } from 'src/redux/slices/Config/userProfile.slice'
// var token = `${localStorage.getItem("accesstoken")}`;

const publicClientApplication = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_AUTH_APP_ID,
    redirectUri: import.meta.env.VITE_AUTH_REDIRECT_URI,
    authority: import.meta.env.VITE_AUTHORITY,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
    secureCookies: false,
  },
})

// AZURE AD login
export const azureADLogin = async () => {
  try {
    await publicClientApplication.loginPopup({
      scopes: import.meta.env.VITE_SCOPES,
      prompt: 'select_account',
    })
    return {
      status: 200,
      data: localStorage,
    }
  } catch (error) {
    return error.message
  }
}

export default function emptyCache() {
  if ('caches' in window) {
    caches.keys().then((names) => {
      // Delete all the cache files
      names.forEach((name) => {
        caches.delete(name)
      })
    })

    // Makes sure the page reloads. Changes are only visible after you refresh.
    // window.location.reload(true)
  }
}

//Logout
export const doLogout = async () => {
  try {
    emptyCache()
    const account = msalInstance.getActiveAccount()
    msalInstance.logoutRedirect({ account: account })
    appInsights.unload()
    return {
      status: 200,
      data: 'Logged Out',
    }
  } catch (error) {
    return error.message
  }
}

export const fetchUser = async (token, dispatch) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/common/get-user`, {
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()

    if (dBdata?.user_roles?.length > 0) {
      dispatch(setRole(dBdata.user_roles))
    }
    // Set currency symbol in bidAnalysisFilter slice if currency_preference exists
    if (dispatch && dBdata?.currency_preference) {
      dispatch(setCurrencySymbol(dBdata.currency_preference === 'USD' ? '$' : '€'))
      dispatch(setBidAnalysisFilters({ targetCurrency: dBdata.currency_preference }))
    }
    if (dispatch && dBdata) {
      const defaultOwnerData = {
        label: dBdata.name || dBdata.preferred_username,
        value: dBdata.email || dBdata.preferred_username,
        email: dBdata.email || dBdata.preferred_username,
        id: dBdata.id || dBdata.oid,
      }
      dispatch(setDefaultOwnerFilter(defaultOwnerData))
    }
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    // navigateHome()
    return error.message
  }
}

export const fetchUserDefaultRoute = async (token) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v1/common/get-user-with-default-route`,
      {
        headers: { Authorization: 'Bearer ' + token },
      },
    )
    if (!response.ok) {
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    // navigateHome()
    return error.message
  }
}

export const fetchAppConfig = async (token) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/app-config/get-config`, {
      headers: { Authorization: 'Bearer ' + token },
    })
    if (!response.ok) {
      if (response.status === 403) {
        console.log(response)
        throw Error(response.statusText)
      }
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    if (error.message === 'Forbidden') {
      // alert('Session Expired!. Please Login Again.')
      doLogout()
      navigateHome()
      console.log(error)
    }
    return error.message
  }
}

export const navigateHome = () => {
  clearAllStorages()
  window.location.href = '/#/'
}

export const clearAllStorages = () => {
  localStorage.clear()
  sessionStorage.clear()
  document.cookie = ''
}

export async function getUserDetailsFromAccessToken(token, dispatch) {
  let response = await fetchUser(token, dispatch)
  if (response.status === 200) {
    return response.data
  }
}

async function getConfigDetails() {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/configurations/app-settings`, {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('accesstoken') },
    })
    const data = await response.json()
    return data
  } catch (err) {
    console.log('err: ', err)
  }
}

export async function setTokensInLocalStorage(dispatch) {
  try {
    const sessionStorageValues = Object.values(sessionStorage)
    const values = sessionStorageValues
      .map((store) => {
        try {
          const parsed = JSON.parse(store)
          return parsed
        } catch {
          return null
        }
      })
      .filter((parsed) => parsed && parsed.credentialType && parsed.secret)

    values.forEach((entry) => {
      if (entry.credentialType === 'IdToken') localStorage.setItem('accesstoken', entry.secret)
      else if (entry.credentialType === 'RefreshToken')
        localStorage.setItem('refreshtoken', entry.secret)
    })

    let user = await getUserDetailsFromAccessToken(localStorage.getItem('accesstoken'), dispatch)
    localStorage.setItem('uname', JSON.stringify(user))

    if (user?.currency_preference && user?.theme_preference && user?.top_down_creation_preference) {
      localStorage.setItem('default_currency', user?.currency_preference)
      localStorage.setItem('theme', user?.theme_preference)
      localStorage.setItem('top_down_creation_preference', user?.top_down_creation_preference)
    } else {
      const response = await getConfigDetails()
      if (user?.top_down_creation_preference) {
        localStorage.setItem('top_down_creation_preference', user?.top_down_creation_preference)
      } else {
        const toDownCreation = response?.data.find(
          (el) => el.name === 'top_down_creation_preference',
        )
        localStorage.setItem('top_down_creation_preference', toDownCreation?.value)
      }
      if (user?.theme_preference) {
        localStorage.setItem('theme', user?.theme_preference)
      } else {
        const theme = response?.data.find((el) => el.name === 'default_theme')
        theme?.value && localStorage.setItem('theme', theme?.value)
      }
      if (user?.currency_preference) {
        localStorage.setItem('default_currency', user?.currency_preference)
      } else {
        const currency = response?.data?.find((el) => el.name === 'default_currency')
        currency?.value && localStorage.setItem('default_currency', currency?.value)
      }
    }
  } catch (error) {
    throw new Error(error)
  }
}
